import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useImperativeHandle, useEffect, useCallback, useMemo } from 'react';
import { nanoid } from 'nanoid';
import { useStyleConfig, Box, Button } from '@chakra-ui/react';
import { EffectCards, EffectCreative, EffectCoverflow, EffectFade, A11y, Keyboard, Navigation, Scrollbar, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
var _excluded = ["slides"];
var Carousel = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var slides = _ref.slides,
      swiperProps = _objectWithoutProperties(_ref, _excluded);

  var _swiperProps$allowTou = swiperProps.allowTouchMove,
      allowTouchMove = _swiperProps$allowTou === void 0 ? true : _swiperProps$allowTou,
      effect = swiperProps.effect,
      id = swiperProps.id,
      _swiperProps$navigati = swiperProps.navigation,
      navigation = _swiperProps$navigati === void 0 ? true : _swiperProps$navigati,
      _swiperProps$paginati = swiperProps.pagination,
      pagination = _swiperProps$paginati === void 0 ? true : _swiperProps$paginati,
      _swiperProps$scrollba = swiperProps.scrollbar,
      scrollbar = _swiperProps$scrollba === void 0 ? false : _swiperProps$scrollba,
      _swiperProps$variant = swiperProps.variant,
      variant = _swiperProps$variant === void 0 ? 'horizontal' : _swiperProps$variant,
      _swiperProps$centered = swiperProps.centeredSlides,
      centeredSlides = _swiperProps$centered === void 0 ? variant === 'centered' : _swiperProps$centered;
  /***
   * State
   */

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      swiperInstance = _useState2[0],
      setSwiperInstance = _useState2[1];

  var _useState3 = useState(true),
      _useState4 = _slicedToArray(_useState3, 2),
      isPrevDisabled = _useState4[0],
      setIsPrevDisabled = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isNextDisabled = _useState6[0],
      setIsNextDisabled = _useState6[1];

  var styles = useStyleConfig('Carousel', {
    variant: [pagination, effect, variant]
  });
  /***
   * Hooks
   */

  useImperativeHandle(ref, function () {
    return {
      slideTo: function slideTo(index) {
        swiperInstance === null || swiperInstance === void 0 || swiperInstance.slideTo(index);
      }
    };
  }, [swiperInstance]);
  useEffect(function () {
    if (swiperInstance) {
      var updateButtonState = function updateButtonState() {
        setIsPrevDisabled(swiperInstance.isBeginning);
        setIsNextDisabled(swiperInstance.isEnd);
      };

      updateButtonState(); // check

      swiperInstance.on('slideChange', updateButtonState);
      swiperInstance.on('reachBeginning', updateButtonState);
      swiperInstance.on('reachEnd', updateButtonState);
      return function () {
        swiperInstance.off('slideChange', updateButtonState);
        swiperInstance.off('reachBeginning', updateButtonState);
        swiperInstance.off('reachEnd', updateButtonState);
      };
    }
  }, [swiperInstance]); // custom nav buttons

  var handlePrevButtonClick = useCallback(function () {
    swiperInstance === null || swiperInstance === void 0 || swiperInstance.slidePrev();
  }, [swiperInstance]);
  var handleNextButtonClick = useCallback(function () {
    swiperInstance === null || swiperInstance === void 0 || swiperInstance.slideNext();
  }, [swiperInstance]);
  /***
   * Methods
   */

  var getSwiperComputedProps = useCallback(function () {
    var modules = [A11y];
    var props = {
      direction: variant === 'vertical' ? 'vertical' : 'horizontal',
      centeredSlides: centeredSlides,
      navigation: navigation
    };

    if (allowTouchMove || navigation || pagination) {
      modules.push(Keyboard);
      props = _objectSpread2(_objectSpread2({}, props), {}, {
        grabCursor: true,
        keyboard: {
          enabled: true
        }
      });

      if (navigation) {
        modules.push(Navigation);
      }

      if (scrollbar) {
        modules.push(Scrollbar);
        props = _objectSpread2(_objectSpread2({}, props), {}, {
          scrollbar: {
            el: '.swiper-scrollbar',
            draggable: true
          }
        });
      } else if (pagination) {
        modules.push(Pagination);
        var paging = pagination === 'dynamic' ? {
          dynamicBullets: true,
          clickable: true
        } : pagination === 'scrollbar' || pagination === 'progress' ? {
          type: 'progressbar'
        } : pagination === 'fraction' ? {
          type: 'fraction'
        } : {
          clickable: true
        };
        props = _objectSpread2(_objectSpread2({}, props), {}, {
          pagination: paging
        });
      }
    }

    if (effect) {
      var effectModules = {
        fade: {
          module: EffectFade,
          options: {
            fadeEffect: {
              crossFade: true
            }
          }
        },
        coverflow: {
          module: EffectCoverflow
        },
        creative: {
          module: EffectCreative,
          options: {
            creativeEffect: {
              prev: {
                shadow: true,
                translate: [0, 0, -400]
              },
              next: {
                translate: ['100%', 0, 0]
              }
            }
          }
        },
        cards: {
          module: EffectCards
        }
      };

      if (effect in effectModules) {
        var _effectModules$effect = effectModules[effect],
            module = _effectModules$effect.module,
            options = _effectModules$effect.options;

        if (options) {
          props = _objectSpread2(_objectSpread2({}, props), options);
        }

        modules.push(module);
      }
    }

    return _objectSpread2(_objectSpread2({}, props), {}, {
      modules: modules
    });
  }, [variant, centeredSlides, navigation, allowTouchMove, pagination, scrollbar, effect]);
  useEffect(function () {
    if (!swiperInstance || !swiperInstance.navigation) {
      return;
    }

    swiperInstance.navigation.init();
    swiperInstance.navigation.update();
  }, [swiperInstance]);
  /**
   * DOM
   */
  // TODO Switch to IconButton

  return /*#__PURE__*/React.createElement(Box, {
    "data-object-type": "core-carousel",
    __css: styles,
    position: "relative"
  }, variant === 'vertical' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    className: "swiper-button-prev ".concat(isPrevDisabled ? 'swiper-button-disabled' : ''),
    onClick: handlePrevButtonClick
  }), /*#__PURE__*/React.createElement(Button, {
    className: "swiper-button-next ".concat(isNextDisabled ? 'swiper-button-disabled' : ''),
    onClick: handleNextButtonClick
  })), /*#__PURE__*/React.createElement(Swiper, _extends({
    a11y: _objectSpread2({
      enabled: true
    }, id && {
      id: id
    })
  }, swiperProps, getSwiperComputedProps(), {
    onSwiper: setSwiperInstance,
    scrollbar: {
      draggable: true,
      el: '.swiper-scrollbar'
    },
    navigation: variant === 'vertical' ? {
      nextEl: 'swiper-button-next',
      prevEl: 'swiper-button-prev'
    } : navigation
  }), useMemo(function () {
    return slides.map(function (slide) {
      return /*#__PURE__*/React.createElement(SwiperSlide, {
        key: "slide-".concat(nanoid())
      }, slide);
    });
  }, [slides]), scrollbar && /*#__PURE__*/React.createElement("div", {
    className: "swiper-scrollbar"
  })));
});
Carousel.displayName = 'CoreCarousel';
export { Carousel };